import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, LockKeyhole } from 'lucide-react';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { AlertBanner } from '@/components/ui/AlertBanner';
import { getApiErrorMessage } from '@/lib/axios';
import { login as loginRequest } from '@/services/auth';
import { useAuthStore } from '@/store/authStore';

const schema = z.object({
  email: z.string().min(1, 'Email requis').email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      const result = await loginRequest(values.email, values.password);
      setSession(result);
      navigate(from, { replace: true });
    } catch (error) {
      setServerError(getApiErrorMessage(error, 'Échec de la connexion.'));
    }
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{ background: 'radial-gradient(circle at 30% 20%, #2D3561 0%, #1B1F3A 55%, #12152b 100%)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <div className="flex size-14 items-center justify-center rounded-2xl bg-brand-accent text-xl font-extrabold text-brand-accent-contrast shadow-lg">
            AW
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-white">Advanced Works ERP</h1>
            <p className="text-xs font-semibold uppercase tracking-widest text-sidebar-text">
              Gestion BTPH · Algérie
            </p>
          </div>
        </div>

        {/* Carte */}
        <div className="rounded-2xl border border-white/10 bg-surface-secondary p-8 shadow-lg">
          <h2 className="mb-1 text-lg font-bold text-content-primary">Connexion</h2>
          <p className="mb-6 text-sm text-content-secondary">Accédez à votre espace de travail.</p>

          {serverError && (
            <AlertBanner variant="error" className="mb-4">
              {serverError}
            </AlertBanner>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="vous@advancedworks-dz.com"
                {...register('email')}
                aria-invalid={!!errors.email}
              />
              {errors.email && <p className="text-xs font-medium text-status-stuck">{errors.email.message}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-content-secondary">
                Mot de passe
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="pr-10"
                  {...register('password')}
                  aria-invalid={!!errors.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-content-muted hover:text-content-secondary"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-xs font-medium text-status-stuck">{errors.password.message}</p>
              )}
            </div>

            <Button type="submit" size="lg" className="mt-2" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="animate-spin" /> : <LockKeyhole />}
              Se connecter
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-sidebar-text">
          © {new Date().getFullYear()} Advanced Works — Oran, Algérie
        </p>
      </div>
    </div>
  );
}
